// components/Charts/FactorsBarChart.jsx

import React, { useRef, forwardRef, useImperativeHandle } from 'react';
import { Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';

// Plugin para valores encima de barras
const valueOnTopPlugin = {
    id: "valueOnTop",
    afterDatasetsDraw(chart) {
        const { ctx } = chart;
        chart.data.datasets.forEach((dataset, datasetIndex) => {
            const meta = chart.getDatasetMeta(datasetIndex);
            meta.data.forEach((bar, index) => {
                const value = dataset.data[index];
                if (value == null) return;

                ctx.save();
                ctx.font = "bold 12px sans-serif";
                ctx.fillStyle = "#000";
                ctx.textAlign = "center";
                ctx.textBaseline = "bottom";

                const x = bar.x;
                const y = bar.y - 5;

                ctx.strokeStyle = "white";
                ctx.lineWidth = 2;
                ctx.strokeText(value, x, y);
                ctx.fillText(value, x, y);
                ctx.restore();
            });
        });
    }
};

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    valueOnTopPlugin
);

// Eliminadas las interfaces FactorsBarChartProps y ChartData

const BarChartFactors = forwardRef(({
    data,
    title = 'Emisiones de CO2 por Factor',
    height = '500px',
    showTitle = true,
    showLegend = false
}, ref) => {
    const canvasRef = useRef(null);
    const chartInstanceRef = useRef(null);

    useImperativeHandle(ref, () => ({
        getCanvas: () => canvasRef.current,
        toggleLegend: (show) => {
            if (chartInstanceRef.current) {
                chartInstanceRef.current.options.plugins.legend.display = show;
                chartInstanceRef.current.update();
            }
        },
        getLegendData: () => {
            if (!data || !data.datasets) return [];
            return data.datasets.map((dataset) => {
                const value = dataset.data[0];
                const unit = dataset.unit || '';
                const color = Array.isArray(dataset.backgroundColor)
                    ? dataset.backgroundColor[0]
                    : dataset.backgroundColor;
                return {
                    label: dataset.label,
                    value: `${value != null ? value.toFixed(2) : ''} ${unit}`,
                    color
                };
            });
        }
    }));

    if (!data || !data.datasets || data.datasets.length === 0) {
        return (
            <div
                style={{
                    height,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    color: '#666',
                    border: '2px dashed #ddd',
                    borderRadius: '8px',
                    backgroundColor: '#f9f9f9',
                    width: '100%'
                }}
            >
                <p>No hay datos de factores disponibles</p>
            </div>
        );
    }

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: showLegend,
                position: 'top', // Quitamos "as const"
            },
            title: {
                display: showTitle,
                text: title,
                font: { size: 16, weight: 'bold' } // Quitamos "as const"
            },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        const value = context.parsed.y;
                        const unit = context.dataset.unit || '';
                        return `${context.dataset.label}: ${value.toFixed(2)} ${unit}`;
                    },
                },
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                title: {
                    display: true,
                    text: 'Valores'
                }
            }
        }
    };

    return (
        <div style={{ height, width: '100%' }}>
            <Bar
                data={data}
                options={options}
                ref={(instance) => {
                    if (instance) {
                        canvasRef.current = instance.canvas;
                        chartInstanceRef.current = instance;
                    }
                }}
            />
        </div>
    );
});

export default BarChartFactors;